import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("admin@namyahealth.com");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background grain">
      {/* Visual side */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-primary text-primary-foreground">
        <div className="relative z-10 px-14 py-16 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="size-12 rounded-md bg-white/95 grid place-items-center overflow-hidden">
              <img src="/logo.png" alt="Namya Health" className="size-11 object-contain" />
            </div>
            <div className="font-heading text-xl font-semibold">Namya Health</div>
          </div>
          <div className="space-y-5">
            <p className="text-xs uppercase tracking-[0.3em] text-primary-foreground/60">
              Admin Console
            </p>
            <h1 className="font-heading text-5xl font-bold tracking-tight leading-[1.05]">
              Care, organized.
              <br />
              Calm, by design.
            </h1>
            <p className="text-primary-foreground/70 max-w-md leading-relaxed">
              Manage patients, appointments, blogs, medications and prescriptions — all in one
              quiet, focused workspace.
            </p>
          </div>
          <div className="text-xs text-primary-foreground/50">
            © {new Date().getFullYear()} Namya Health — Wellness Hub
          </div>
        </div>
        <div
          aria-hidden
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.25), transparent 50%), radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15), transparent 50%)",
          }}
        />
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="size-12 rounded-md bg-primary/5 grid place-items-center overflow-hidden">
              <img src="/logo.png" alt="Namya Health" className="size-11 object-contain" />
            </div>
            <div className="font-heading text-lg font-semibold">Namya Health</div>
          </div>

          <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold mb-3">
            Sign in
          </p>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">
            Welcome back
          </h2>
          <p className="text-muted-foreground mb-8 text-sm">
            Access your clinic dashboard.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@namyahealth.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full"
              size="lg"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-6">
            Use the admin credentials seeded for your environment.
          </p>
        </div>
      </div>
    </div>
  );
}
